import { onAuthStateChanged } from "firebase/auth"
import { auth } from "../app/App"
import { Agent } from "../types/Agent"
import { Endpoints, Method, ReturnTypes, SecondPathParams } from "../types/Api"
import { Avatar } from "../types/Avatar"
import { Call } from "../types/Call"
import { Feedback } from "../types/Feedback"
import { UsageDetails } from "../types/UsageDetails"

export const isLocalDev = process.env.NODE_ENV === "development"

export const isDev = isLocalDev || window.location.href.includes("dev")

export const isStaging = window.location.href.includes("staging")

export const isProduction = !isLocalDev && !isDev && !isStaging

export const backendUrl = isLocalDev
  ? "http://127.0.0.1:8000/"
  : isDev
    ? "https://api-dev.bey.chat/"
    : isStaging
      ? "https://api-staging.bey.chat/"
      : "https://api.bey.chat/"

export interface VerifyTokenResponse {
  uid: string
  email: string
  name: string
  customerId?: string
  subscriptionState?: string
  subscriptionPlan?: string
  customToken?: string
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: any,
  ) {
    super(message)
    this.name = "ApiError"
  }
}

const waitForAuth = (): Promise<void> => {
  return new Promise((resolve) => {
    if (auth.currentUser) {
      resolve()
      return
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        unsubscribe()
        resolve()
      }
    })

    setTimeout(() => {
      unsubscribe()
      resolve()
    }, 5000)
  })
}

const callApi = async (
  endpoint: Endpoints,
  method = Method.GET,
  id?: string,
  body?: any,
  secondPathParam?: SecondPathParams,
  returnType = ReturnTypes.json,
  token?: string,
) => {
  let authToken = token

  if (!authToken) {
    if (!auth.currentUser) {
      await waitForAuth()
    }
    authToken = auth.currentUser ? await auth.currentUser.getIdToken() : null
  }

  if (!authToken) {
    throw new ApiError("Authentication failed - no token available", 401)
  }

  const response = await fetch(
    secondPathParam && !id
      ? `${backendUrl}${endpoint}/${secondPathParam}`
      : secondPathParam
        ? `${backendUrl}${endpoint}/${id}/${secondPathParam}`
        : id
          ? `${backendUrl}${endpoint}/${id}`
          : `${backendUrl}${endpoint}`,
    {
      method,
      body: body ? JSON.stringify(body) : undefined,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
    },
  )

  if (response.status !== 200) {
    const json = await response.json()
    throw new ApiError(
      (json as any).detail || `HTTP Error ${response.status}`,
      response.status,
      json,
    )
  }
  if (returnType === ReturnTypes.blob) {
    return response.blob()
  } else {
    return response.json()
  }
}

export const verifyToken = async (
  token: string,
): Promise<VerifyTokenResponse> => {
  return callApi(
    Endpoints.creator,
    Method.POST,
    null,
    undefined,
    SecondPathParams.verifyToken,
    undefined,
    token,
  )
}

export const postOffer = async (body: any) => {
  return callApi(Endpoints.offer, Method.POST, undefined, body)
}

export const getVideo = async (id: string) => {
  return callApi(Endpoints.video, Method.GET, `${id}?${Date.now()}`)
}

export const getLivekitToken = async (room_id: string) => {
  return callApi(Endpoints.getToken, Method.GET, room_id)
}

export const deleteLivekitRoom = async (room_id: string) => {
  return callApi(Endpoints.deleteRoom, Method.POST, room_id, { room_id })
}

export const getCapacity = async () => {
  return callApi(Endpoints.capacity, Method.GET)
}

export const getAgentUsage = async (
  agent_id: string,
): Promise<UsageDetails> => {
  return callApi(
    Endpoints.agent,
    Method.GET,
    agent_id,
    undefined,
    SecondPathParams.usage,
  )
}

export const verifyAgentExists = async (agentId: string): Promise<boolean> => {
  try {
    await getAgent(agentId)
    return true
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return false
    console.error("Error verifying agent:", error)
    return false
  }
}

export const getAgent = async (agent_id: string): Promise<Agent> => {
  return callApi(
    Endpoints.agent,
    Method.GET,
    agent_id,
    undefined,
    SecondPathParams.public,
  )
}

export const getCall = async (call_id: string): Promise<Call> => {
  return callApi(Endpoints.call, Method.GET, call_id)
}

export const createFeedback = async (feedback: Partial<Feedback>) => {
  return callApi(Endpoints.feedback, Method.POST, undefined, feedback)
}

export const getAvatar = async (avatarId: string): Promise<Avatar> => {
  return callApi(Endpoints.avatar, Method.GET, avatarId)
}

export const getDefaultAgentId = "defaultAgent"
export const getDefaultCreatorId = "felix"
